export interface Question {
  id: string;
  question: string;
  options: string[];
  /** 1-based index of the correct option, as written in the source PDFs. */
  correctAnswerIndex: number;
  imagePath?: string | null;
  group?: number;
}

export interface Quiz {
  id: number;
  name: string;
  questions: Question[];
}

export type QuizMode = 'exam' | 'practice';

/**
 * Which question bank a test comes from: one licence category of the 2026 edition, which
 * roadpolice.am publishes as two separate banks. (The 2022 tests were removed.)
 */
export const V2_CATEGORIES = ['abc', 'dt'] as const;
export type QuizCategory = (typeof V2_CATEGORIES)[number];

export type QuizBank = { edition: 'v2'; category: QuizCategory };

export const DEFAULT_BANK: QuizBank = { edition: 'v2', category: V2_CATEGORIES[0] };

/** Bank of a `quiz/v2/:category` route; null for an unknown or missing category. */
export function bankFromCategory(category: string | null | undefined): QuizBank | null {
  return category && (V2_CATEGORIES as readonly string[]).includes(category)
    ? { edition: 'v2', category: category as QuizCategory }
    : null;
}

/** Stable name of a bank for storage keys and analytics: "v2-abc", "v2-dt". */
export function bankKey(bank: QuizBank): string {
  return `v2-${bank.category}`;
}

/** Path of a bank's test list, e.g. "/quiz/v2/abc", "/ru/quiz/v2/dt". */
export function quizListPath(language: string, bank: QuizBank): string {
  const prefix = language === 'am' ? '' : `/${language}`;
  return `${prefix}/quiz/v2/${bank.category}`;
}

/** Official theory exam rules: 20 questions, 30 minutes, at most 2 mistakes. */
export const EXAM_DURATION_SECONDS = 30 * 60;
export const MAX_ALLOWED_MISTAKES = 2;

export function minPassingScore(totalQuestions: number): number {
  return Math.max(totalQuestions - MAX_ALLOWED_MISTAKES, 0);
}

/** A question option split into its number badge ("1.") and its text. */
export interface OptionView {
  label: string;
  text: string;
}

export function toOptionView(option: string, index: number): OptionView {
  const match = /^\s*(\d+)\s*[.)]\s*(.*)$/s.exec(option);
  if (match && match[2]) {
    return { label: match[1], text: match[2].trim() };
  }
  return { label: String(index + 1), text: option.trim() };
}
